// src/api/brands.ts
import { apiFetch } from "./api-fetch";

export interface AssignableBrand {
  brand_name: string;
  source: string;                 // 'onboarding' | 'monitoring'
  last_activity?: string | null;
}

export interface UserBrands {
  user_id: number;
  username: string;
  full_name?: string | null;
  brands: string[];
}

export interface BrandSummary {
  brand_name: string;
  /** Number of monitoring runs. 0 ⇒ no analysis yet (non-loadable). */
  run_count: number;
}

export interface MyBrands {
  brands: string[];
  active_brand: string | null;
  is_admin: boolean;
  brand_summaries: BrandSummary[];
}

export interface PostOnboardingMonitorResponse {
  brand_name: string;
  monitoring_run_id: number;
  assigned_to?: string | null;
}

export interface BrandRunSummary {
  id: number;
  brand_name: string;
  selected_models: string[];
  status: string;
  created_at?: string | null;
  score?: number | null;
  rec_high: number;
  rec_medium: number;
  rec_low: number;
  rec_total: number;
}

export interface BrandRunDetail extends BrandRunSummary {
  data: any;
}

export interface UserBrandsSummary {
  user_id: number;
  brands: string[];
  count: number;
}

/** Result of a brand/user deletion (or its dry-run preview). `impact` maps
 *  "table.column" -> rows affected; `total_rows` is their sum. */
export interface DeletionImpact {
  target: string;
  dry_run: boolean;
  impact: Record<string, number>;
  total_rows: number;
}

export const brandsApi = {
  // ── Admin ──
  assignable: (q?: string) =>
    apiFetch<AssignableBrand[]>(
      `/brands/assignable${q ? `?q=${encodeURIComponent(q)}` : ""}`,
    ),

  getUserBrands: (userId: number) =>
    apiFetch<UserBrands>(`/brands/users/${userId}`),

  usersSummary: () =>
    apiFetch<UserBrandsSummary[]>("/brands/users-summary"),

  setUserBrands: (userId: number, brands: string[]) =>
    apiFetch<UserBrands>(`/brands/users/${userId}`, {
      method: "PUT",
      body: JSON.stringify({ brands }),
    }),

  // ── Destructive admin purges (hard delete + dry-run preview) ──
  // Preview only — nothing is committed; returns the per-table row counts.
  previewDeleteBrand: (brand: string) =>
    apiFetch<DeletionImpact>(
      `/brands/data?brand=${encodeURIComponent(brand)}&dry_run=true`,
      { method: "DELETE" },
    ),

  // Permanently delete ALL data for a brand across every table. Irreversible.
  deleteBrand: (brand: string) =>
    apiFetch<DeletionImpact>(
      `/brands/data?brand=${encodeURIComponent(brand)}&dry_run=false`,
      { method: "DELETE" },
    ),

  // Preview what deleting a user would remove/null. Nothing is committed.
  // The actual user delete is authApi.deleteUser() (it also revokes tokens).
  previewDeleteUser: (userId: number) =>
    apiFetch<DeletionImpact>(`/brands/users/${userId}/data?dry_run=true`, {
      method: "DELETE",
    }),

  runOnboardingMonitoring: (
    customerId: number,
    body: { selected_models?: string[]; assign_to_username?: string } = {},
  ) =>
    apiFetch<PostOnboardingMonitorResponse>(
      `/brands/onboarding/${customerId}/run-monitoring`,
      { method: "POST", body: JSON.stringify(body) },
    ),

  // ── End user ──
  myBrands: () => apiFetch<MyBrands>("/brands/me"),

  setActive: (brand_name: string) =>
    apiFetch<MyBrands>("/brands/me/active", {
      method: "POST",
      body: JSON.stringify({ brand_name }),
    }),

  // Run history for an assigned brand (brand-scoped — works across users).
  listRuns: (brand: string, limit = 20) =>
    apiFetch<BrandRunSummary[]>(
      `/brands/runs?brand=${encodeURIComponent(brand)}&limit=${limit}`,
    ),

  getRun: (runId: number, brand: string) =>
    apiFetch<BrandRunDetail>(
      `/brands/runs/${runId}?brand=${encodeURIComponent(brand)}`,
    ),
};